import type { EventDetailMarketDto } from '@/lib/models';

/** Market statuses in which trading has permanently ended. */
const TERMINAL_MARKET_STATUSES = new Set(['CLOSED', 'SETTLED', 'CANCELLED']);

/** True when trading on this market has ended (closed = resolved; PRD §3.1). */
export function isMarketClosed(status: string): boolean {
  return TERMINAL_MARKET_STATUSES.has(status.toUpperCase());
}

/**
 * True when the whole event page should render its closed state: every market
 * has stopped trading (PRD §7.1 — buy/sell removed, trading sections replaced
 * by Result). A mixed event (some markets still live) keeps the live layout.
 */
export function isEventClosed(markets: Pick<EventDetailMarketDto, 'status'>[]): boolean {
  return markets.length > 0 && markets.every((m) => isMarketClosed(m.status));
}

/** The full-match moneyline contracts (2-way and 3-way) — mirrors the api's sports classifier. */
const MONEYLINE_CONTRACTS = new Set(['moneyline', 'moneyline_3way']);

/**
 * The markets whose winners a SPORTS event's Result section shows: the match
 * outcome, not every prop market's. Preference order — the moneyline(s), else
 * the HEADLINE game-line markets (spread / total; some matches carry no
 * moneyline in the catalog), else all markets (winner/futures events, whose
 * single WINNER market IS the result).
 */
export function resultMarketsOf<M extends Pick<EventDetailMarketDto, 'contract' | 'contractCategory'>>(
  eventType: string,
  markets: M[],
): M[] {
  if (eventType !== 'SPORTS') return markets;
  const moneylines = markets.filter((m) => m.contract != null && MONEYLINE_CONTRACTS.has(m.contract));
  if (moneylines.length > 0) return moneylines;
  const headline = markets.filter((m) => m.contractCategory === 'HEADLINE');
  return headline.length > 0 ? headline : markets;
}
